import type { DecisionGraphType, DecisionNode } from 'giorules-jdm-editor';
import {
  type CareGapNotificationContent,
  GroupedRetrievedDoc,
  RuleType,
  RulesetType,
  ScoreDetailSource,
  ScoreDetailType,
} from '@tempus/trials-types';

export interface BaseDto {
  id: string;
  createdAt: string;
  updatedAt: string;
}

export type ActiveStatus = 'active' | 'inactive';

export const ACTIVE_CARE_PATHWAY_STATUSES: ActiveStatus[] = ['active'];

/** Ruleset lifecycle. Distinct from `ActiveStatus`, which care pathways and rules still use. */
export type RulesetStatus = 'draft' | 'published' | 'archived';

export enum NextPlatformProgram {
  NEXT_ONCO = 'Next Onco',
  NEXT_ID = 'NextID',
  NEXT_NODULE = 'Next Nodule',
  SITE_TRIALS = 'Site Trials',
  TIME_TRIALS = 'TIME Trials',
  /** Care pathway projects only; not shown in trial program picker */
  PATHWAYS = 'Pathways',
}

export type RuleMetadataType = RuleType;

type RuleMetadataFields = {
  type: RuleMetadataType;
  isDiseaseStateRule: boolean;
  name?: string;
  displayText?: string;
  inclusionKind?: 'inclusion' | 'exclusion';
  displayElementIds?: string[];
  jdmExpressionNodeId?: string;
  isRecurring?: boolean;
  isScaffold?: boolean;
  expressionType?: 'required' | 'supporting';
  dataSources?: string[];
};

/** Rule metadata as the API returns it. `name` and `expressionType` are server-derived. */
export type RuleMetadata = Record<string, unknown> & RuleMetadataFields;

/**
 * Rule metadata a client may send.
 *
 * `name` is deliberately absent: the rule name is the top-level `name` field, re-derived
 * from the graph node on the server, which strips `metadata.name` from request bodies.
 * Sending it does nothing.
 */
type RuleMetadataInputFields = Omit<RuleMetadataFields, 'name'>;

/** Create requires the author's intent explicitly; the server no longer infers either field. */
export type CreateRuleMetadata = RuleMetadataInputFields & {
  inclusionKind: 'inclusion' | 'exclusion';
  expressionType: 'required' | 'supporting';
};

/** Update is partial: an omitted key means leave unchanged. */
export type UpdateRuleMetadata = Partial<RuleMetadataInputFields>;

export type RulesetMetadata = Record<string, unknown> & {
  type?: RulesetType;
  // GAEL fields.
  builderModeDefaultsApplied?: boolean;
  attachedAgentIds?: string[];
  evalAgentPhase?: 'after_rules';
};

export interface BiomarkerResponseDto extends BaseDto {
  name: string;
  status: ActiveStatus;
}

export interface DiseaseStageResponseDto extends BaseDto {
  name: string;
  ordinal: number;
  status: ActiveStatus;
}

export interface DataSourceResponseDto extends BaseDto {
  view: string;
  source: string;
  type: string | null;
  display: string | null;
  isBase: boolean;
}

export interface CarePathwayStageResponseDto {
  id: string;
  name: string;
  ordinal: number;
  status: ActiveStatus;
  rulesetCount: number;
}

export interface CreatePathwayStageReqBody {
  diseaseStageId: string;
  ordinal?: number;
}

export interface UpdatePathwayStageReqBody {
  ordinal?: number;
  status?: ActiveStatus;
}

export interface CarePathwayResponseDto extends BaseDto {
  name: string;
  status: ActiveStatus;
  program: NextPlatformProgram | null;
  description: string | null;
  alertType: string[] | null;
  // Optional: the backend returns `undefined` until the `diagnosis_tmos` column is merged
  // from the other branch. Runtime code uses `?? []`, so `optional` models reality accurately.
  diagnosisTmos?: string[] | null;
  closedReasons?: string[] | null;
  sourceDocuments: Record<string, unknown>[] | null;
  createdBy: string | null;
  lastUpdatedBy: string | null;
  userId?: string | null;
}

export interface CreateCarePathwayReqBody {
  name: string;
  status: ActiveStatus;
  program?: NextPlatformProgram;
  description?: string;
  alertType?: string[];
  diagnosisTmos?: string[];
  closedReasons?: string[];
  sourceDocuments?: Record<string, unknown>[];
}

export interface UpdateCarePathwayReqBody {
  name?: string;
  status?: ActiveStatus;
  program?: NextPlatformProgram;
  description?: string;
  alertType?: string[];
  diagnosisTmos?: string[];
  closedReasons?: string[];
  sourceDocuments?: Record<string, unknown>[];
}

export const BUILDER_TRIALS_DEFAULT_PAGE = 1;
export const BUILDER_TRIALS_DEFAULT_PAGE_SIZE = 15;
export const BUILDER_TRIALS_MAX_PAGE_SIZE = 100;

export interface BuilderTrialResponseDto extends BaseDto {
  program: NextPlatformProgram;
  shortname: string;
  title: string;
  nctNumber: string | null;
  tempusTrialId: string;
}

export interface BuilderTrialsQueryParams {
  page?: number;
  pageSize?: number;
  search?: string;
}

export interface BuilderTrialsListResponseDto {
  data: BuilderTrialResponseDto[];
  count: number;
}

export interface CreateBuilderTrialReqBody {
  trialId: string;
}

/**
 * Project shape consumed by shared Builder UI. `shortName` is only populated for trials, whose
 * `name` is a long clinical title that does not fit compact surfaces such as breadcrumbs.
 */
export type BuilderProjectDetail = CarePathwayResponseDto & { shortName?: string };

/** Maps builder trial detail to the care-pathway project shape used by shared Builder UI. */
export function mapBuilderTrialToProjectDetail(trial: BuilderTrialResponseDto): BuilderProjectDetail {
  return {
    id: trial.id,
    createdAt: trial.createdAt,
    updatedAt: trial.updatedAt,
    name: trial.title,
    shortName: trial.shortname,
    status: 'active',
    program: trial.program,
    // Raw NCT id (already prefixed by the source system). Do not add an "NCT " label here.
    description: trial.nctNumber,
    alertType: null,
    closedReasons: null,
    sourceDocuments: null,
    createdBy: null,
    lastUpdatedBy: null,
  };
}

export interface NotificationTemplateGuideline {
  text: string;
  url?: string;
}

export interface NotificationTemplateResponseDto {
  id: string;
  createdAt: string;
  updatedAt: string;
  carePathwayStageId: string;
  name: string;
  notificationText: string;
  guideline: NotificationTemplateGuideline | null;
  userId: string | null;
  userName?: string | null;
  carePathwayName: string;
  stageName: string;
  /** Pathways or Next ID when set on this template. Other programs stay unset. */
  program?: NextPlatformProgram | null;
}

export interface CreateNotificationTemplateReqBody {
  carePathwayStageId: string;
  name: string;
  notificationText: string;
  guideline?: NotificationTemplateGuideline;
  program?: NextPlatformProgram;
}

export interface UpdateNotificationTemplateReqBody {
  name?: string;
  notificationText?: string;
  guideline?: NotificationTemplateGuideline | null;
  program?: NextPlatformProgram;
}

export interface RulesetResponseDto extends BaseDto {
  careGapId: string;
  careGapName: string | null;
  carePathwayId: string | null;
  carePathwayStageId: string | null;
  stageId: string | null;
  stageName: string | null;
  stageOrdinal: number | null;
  biomarkerId: string | null;
  biomarkerName: string | null;
  name: string | null;
  description: string | null;
  status: RulesetStatus;
  version: string;
  metadata: RulesetMetadata;
  /** Generated eval graph, derived from `rulesetDefWritten` on every save. Read-only — clients never assign it. */
  rulesetDef: DecisionGraphType;
  /** Authored graph — the one Builder edits, and the only graph clients write. */
  rulesetDefWritten: DecisionGraphType;
  ruleCount: number | null;
  recentRuns: Record<string, unknown>[];
  userId?: string | null;
  patientQueryEnabled?: boolean;
  promptsUpdatedAt?: string | null;
  /** Trial arm rulesets only; from `BuilderArmRulesetResponseDto.armProgramType`. */
  armProgramType?: number | null;
}

export interface CreateRulesetReqBody {
  careGapId?: string;
  carePathwayId?: string;
  carePathwayStageId?: string;
  biomarkerId?: string;
  status: RulesetStatus;
  version: string;
  name?: string;
  description?: string;
  metadata?: RulesetMetadata;
  rulesetDef?: DecisionGraphType;
  rulesetDefWritten?: DecisionGraphType;
  recentRuns?: Record<string, unknown>[];
  patientQueryEnabled?: boolean;
}

export interface UpdateRulesetReqBody {
  careGapId?: string;
  carePathwayId?: string;
  carePathwayStageId?: string;
  biomarkerId?: string;
  name?: string;
  description?: string;
  status?: RulesetStatus;
  version?: string;
  metadata?: RulesetMetadata;
  rulesetDef?: DecisionGraphType;
  rulesetDefWritten?: DecisionGraphType;
  recentRuns?: Record<string, unknown>[];
  patientQueryEnabled?: boolean;
}

/** Builder arm ruleset — trial-domain API response (no pathway field aliases). */
export interface BuilderArmRulesetResponseDto extends BaseDto {
  trialId: string;
  armId: string;
  armName: string;
  /** TIME vs Site Trials — from the parent arm's program type. */
  armProgramType: number;
  name: string | null;
  description: string | null;
  /** Record lifecycle (`active` / `inactive`), not publish workflow. */
  status: ActiveStatus;
  publishStatus: RulesetStatus | null;
  version: string;
  metadata: RulesetMetadata;
  /** Generated eval graph, derived from `rulesetDefWritten` on every save. Read-only — clients never assign it. */
  rulesetDef: DecisionGraphType;
  /** Authored graph — the one Builder edits, and the only graph clients write. */
  rulesetDefWritten: DecisionGraphType;
  ruleCount: number | null;
  recentRuns: Record<string, unknown>[] | null;
  userId?: string | null;
  patientQueryEnabled?: boolean;
  promptsUpdatedAt?: string | null;
}

export interface UpdateBuilderArmRulesetReqBody {
  name?: string;
  description?: string;
  status?: ActiveStatus;
  publishStatus?: RulesetStatus;
  version?: string;
  metadata?: RulesetMetadata;
  rulesetDefWritten?: DecisionGraphType;
  /** The endpoint 400s on a client-supplied generated graph; keep that a compile error rather than a runtime one. */
  rulesetDef?: never;
  patientQueryEnabled?: boolean;
}

/** Maps trial arm ruleset API shape to the shared Builder ruleset view model. */
export function mapArmRulesetToRulesetViewModel(dto: BuilderArmRulesetResponseDto): RulesetResponseDto {
  return {
    id: dto.id,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
    carePathwayId: dto.trialId,
    careGapId: dto.armId,
    careGapName: dto.armName,
    armProgramType: dto.armProgramType,
    carePathwayStageId: null,
    stageId: null,
    stageName: null,
    stageOrdinal: null,
    biomarkerId: null,
    biomarkerName: null,
    name: dto.name,
    description: dto.description,
    status: dto.publishStatus ?? 'draft',
    version: dto.version,
    metadata: dto.metadata,
    rulesetDef: dto.rulesetDef,
    rulesetDefWritten: dto.rulesetDefWritten,
    ruleCount: dto.ruleCount,
    recentRuns: dto.recentRuns ?? [],
    userId: dto.userId ?? null,
    patientQueryEnabled: dto.patientQueryEnabled,
    promptsUpdatedAt: dto.promptsUpdatedAt,
  };
}

/** Strips care-pathway-only fields before calling the arm ruleset update endpoint. */
export function toUpdateBuilderArmRulesetReqBody(body: UpdateRulesetReqBody): UpdateBuilderArmRulesetReqBody {
  const { name, description, status, version, metadata, rulesetDefWritten, patientQueryEnabled } = body;
  return { name, description, publishStatus: status, version, metadata, rulesetDefWritten, patientQueryEnabled };
}

export interface DuplicateRulesetReqBody {
  careGapId?: string;
  carePathwayId?: string;
  carePathwayStageId?: string;
  biomarkerId?: string;
  name?: string;
}

export interface RuleResponseDto extends BaseDto {
  rulesetId: string;
  name: string | null;
  description: string | null;
  category: string | null;
  ruleDef: DecisionNode;
  metadata: RuleMetadata;
  status: ActiveStatus;
  version: string;
  userId?: string | null;
}

export type PatientQueryIecType = 'inclusion' | 'exclusion';

export interface PatientQueryPromptResponseDto extends BaseDto {
  ruleId: string;
  prompt: string;
  tag: string;
  iecType: PatientQueryIecType;
  userId: string;
}

export const PATIENT_QUERY_PROMPT_TAG_PATTERN = /^[A-Za-z0-9_]+$/;

export interface CreatePatientQueryPromptReqBody {
  prompt: string;
  tag: string;
  iecType: PatientQueryIecType;
}

export interface UpdatePatientQueryPromptReqBody {
  prompt?: string;
  tag?: string;
  ruleId?: string;
  iecType?: PatientQueryIecType;
}

export type PatientQueryDomain = 'next' | 'time';

export type PatientQueryJobStatus = 'queued' | 'completed' | 'failed';

export type PatientQueryTriggerType = 'builder' | 'nightly';

export interface PatientQueryPatient {
  patientLinkageId: string;
  institutionId: string;
}

export interface CreatePatientQueryJobReqBody {
  patients: PatientQueryPatient[];
}

export interface PatientQueryPromptSnapshotEntry {
  promptId: string;
  prompt: string;
  tag: string;
  iecType: 'inclusion' | 'exclusion';
  lastModified: string;
}

export interface PatientQueryJobResponseDto extends BaseDto {
  rulesetId: string;
  status: PatientQueryJobStatus;
  triggerType: PatientQueryTriggerType;
  promptSnapshot: PatientQueryPromptSnapshotEntry[];
  patientCount: number;
  userId: string;
  // Per-patient results are included by the job-detail endpoint, but not by list/create responses.
  results?: PatientQueryJobResultsPage;
}

export interface PatientQueryJobsResponseDto {
  data: PatientQueryJobResponseDto[];
  count: number;
}

// One criterion's answer for a single patient in a Builder APQ test run.
export interface PatientQueryJobResultDetail {
  patientLinkageId: string;
  tag: string;
  iecSatisfied: boolean | null;
  type: ScoreDetailType | null;
  query: string | null;
  summary: string | null;
  reasoning: string | null;
  finalAnswer: string | null;
  failureReason: string | null;
  groupedRetrievedDocs: GroupedRetrievedDoc[];
  sources: ScoreDetailSource[];
  updatedAt: string;
  promptId: string;
}

// Per-patient results for a Builder APQ test run
export interface PatientQueryJobPatientResults {
  patientLinkageId: string;
  institutionId: string;
  score: number;
  passedCount: number;
  totalPrompts: number;
  details: PatientQueryJobResultDetail[];
}

// Paged per-patient results embedded in a Builder APQ job detail response.
export interface PatientQueryJobResultsPage {
  data: PatientQueryJobPatientResults[];
  count: number;
  page: number;
  pageSize: number;
}

export interface CreateRuleReqBody {
  ruleDef?: DecisionNode;
  metadata: CreateRuleMetadata;
  status: ActiveStatus;
  version: string;
  /** Required: the server rejects a missing or whitespace-only name rather than defaulting it. */
  name: string;
  description?: string;
  category?: string;
}

export interface UpdateRuleReqBody {
  name?: string;
  description?: string;
  category?: string;
  ruleDef?: DecisionNode;
  metadata?: UpdateRuleMetadata;
  status?: ActiveStatus;
  version?: string;
}

// export type RuleCategory = 'Disease' | 'Labs' | 'Therapy' | 'Biomarkers' | 'Active Patient' | 'Medications';

export type RunTestMode = 'cohort' | 'patientLinkageIds';

export interface EvalEngineCohortResponseDto {
  cohortId: string;
  name: string;
  description: string | null;
  patientLinkageIds: string[];
  createdAt: string;
  updatedAt: string;
}

export interface EvalJobDto extends BaseDto {
  rulesetIds: string[];
  metadata: Record<string, unknown>;
  finished: boolean;
  errors?: Record<string, unknown[]>;
  totalPatients?: number;
  processedPatientCount?: number;
  stuckReason?: string | null;
}

export interface EvalJobPatientResultDto {
  patientLinkageId: string;
  result: Record<string, unknown> | null;
  errors: Record<string, unknown> | null;
  dpId: string | null;
}

/** Normalized consumer shape (`EvalJobDto` + `results`). Live wire may be `{ job, items }`. */
export interface EvalJobDetailResponseDto extends EvalJobDto {
  results: EvalJobPatientResultDto[];
}

/** Live GET /rulesets/:id/evaluation-jobs/:jobId body (eval-engine proxy). */
export type EvalJobDetailWireDto =
  | (EvalJobDto & {
      results?: EvalJobPatientResultDto[];
      items?: EvalJobPatientResultDto[];
    })
  | {
      job: EvalJobDto;
      items?: EvalJobPatientResultDto[];
      results?: EvalJobPatientResultDto[];
      errors?: unknown[];
      page?: number;
      pageSize?: number;
      hasMore?: boolean;
    };

export function mapEvalJobDetailResponse(data: EvalJobDetailWireDto): EvalJobDetailResponseDto {
  if ('job' in data) {
    return { ...data.job, results: data.results ?? data.items ?? [] };
  }
  const { items, results, ...job } = data;
  return { ...job, results: results ?? items ?? [] };
}

export interface EvalJobsResponseDto {
  data: EvalJobDto[];
  count: number;
}

export interface SubmitEvaluationReqBody {
  rulesetIds: string[];
  institutionId?: string;
  cohortId?: string;
  patientLinkageIds?: string[];
}

export interface SubmitEvaluationResponseDto {
  message: string;
  jobIds: string[];
}

export interface SubmitArmRulesetEvaluationReqBody {
  patientLinkageIds: string[];
}

export interface SubmitArmRulesetEvaluationResponseDto {
  message: string;
  runId: string;
}

// Simulate — POST /rulesets/:id/simulate. Runs a JDM graph through the eval engine
// (Zen) for a live builder preview. Both fields optional; extra fields are stripped.
export interface SimulateRulesetReqBody {
  /** JDM decision graph to simulate. Omit to run the ruleset's persisted `rulesetDef`. */
  graph?: DecisionGraphType;
  /** Sample input context fed to the graph. Defaults to `{}` server-side. */
  context?: Record<string, unknown>;
}

// Per-node execution trace, keyed by node id in the simulate response/error.
export interface ZenNodeTrace {
  id: string;
  name: string;
  input: unknown;
  output: unknown;
  order: number;
  performance: string;
  traceData: unknown | null;
}

export interface SimulateRulesetResponseDto {
  /** Graph's evaluated output — untyped; shape depends on the graph's output node(s). */
  result: Record<string, unknown>;
  performance: string;
  trace?: Record<string, ZenNodeTrace>;
}

// 400 body forwarded verbatim from Zen when a node fails to evaluate — the important
// case for the builder (highlight `nodeId` in the editor). Distinct from the standard
// Nest error shape returned for an invalid id, so branch via `getSimulateRulesetNodeError`.
export interface SimulateRulesetNodeError {
  nodeId: string;
  source: string;
  trace: Record<string, ZenNodeTrace>;
  type: 'NodeError';
}

// Agents registry — /agents
export interface AgentResponseDto extends BaseDto {
  agentType: string;
  specVersion: string;
}

export interface CreateAgentDto {
  agentType: string;
  specVersion?: string;
}

export interface UpdateAgentDto {
  agentType?: string;
  specVersion?: string;
}

// Ruleset agents — /rulesets/:rulesetId/agents (ordered by ordinal)
export interface RulesetAgentResponseDto extends BaseDto {
  rulesetId: string;
  agentType: string;
  ordinal: number;
}

export interface CreateRulesetAgentDto {
  agentType: string;
  ordinal?: number;
}

export interface UpdateRulesetAgentDto {
  ordinal?: number;
}

export interface RuleOutputFieldResponseDto extends BaseDto {
  agentType: string;
  outputField: string;
}

export interface SetRuleOutputFieldDto {
  agentType: string;
  outputField: string;
}

export interface SetRuleOutputFieldsDto {
  outputFields: SetRuleOutputFieldDto[];
}

export type RulesetOutputElements = Record<string, string[]>;

export type ValidationSeverity = 'error' | 'warning';

// A single validation problem. `code` is a stable machine id; `severity`
// separates publish-blockers from advisories; `nodeId` / `path` locate it for
// deep-linking into the graph node or field.
export interface ValidationIssue {
  code: string;
  message: string;
  severity: ValidationSeverity;
  nodeId?: string;
  path?: string;
}

// Canonical ruleset validation result. Returned at 200 by the `/validate`
// dry-run and, via `parseBuilderError`, carried on the publish 422. `valid` is
// true (no error-severity issues) when the ruleset is publishable.
export interface RulesetValidationResult {
  message: string;
  valid: boolean;
  issues: ValidationIssue[];
}

export type BuilderApiError =
  | { kind: 'conflict'; message: string; rulesetVersion?: string }
  | { kind: 'validation'; message: string; issues?: ValidationIssue[] }
  | { kind: 'unknown'; message: string; raw?: unknown };

type BuilderErrorResponseData = { message?: string; issues?: ValidationIssue[]; rulesetVersion?: string };

// Map an unknown error (typically AxiosError) into a discriminated BuilderApiError.
export function parseBuilderError(error: unknown): BuilderApiError {
  const e = error as { response?: { status?: number; data?: unknown }; message?: string };
  const status = e?.response?.status;
  const data = e?.response?.data as BuilderErrorResponseData | undefined;
  const message = data?.message ?? e?.message ?? 'Unknown error';

  if (status === 409) {
    return { kind: 'conflict', message, rulesetVersion: data?.rulesetVersion };
  }
  if (status === 422) {
    return { kind: 'validation', message, issues: data?.issues };
  }
  return { kind: 'unknown', message, raw: error };
}

// Care gap site settings preview — mirrors the backend
// `CareGapSiteSettingsPreviewResponseDto` shape exactly.
export type CareGapEnabledBy = 'default' | 'siteSetting';

export interface PreviewCareGapDto {
  careGapId: string;
  careGapName: string;
  carePathwayId: string;
  carePathwayName: string;
  stageName: string;
  enabledBy: CareGapEnabledBy;
  isEnabled: boolean;
  cutoverDate: string | null;
  /** Whether a `care_gap_site_settings` record backs this row. Rows that only inherit care gap defaults are not editable. */
  hasSiteSetting: boolean;
  /** The record's stored override, `null` when the row inherits `cutoverDate` from the care gap default. */
  siteSettingCutoverDate: string | null;
  /** The record's stored flag, `null` when the row inherits `isEnabled` from the care gap default. */
  siteSettingIsEnabled: boolean | null;
}

/**
 * The `care_pathway_sites` record backing one pathway group. `status` here is the
 * site's membership status for the pathway, not the care pathway's own status.
 */
export interface PreviewCarePathwaySiteDto {
  id: string;
  status: ActiveStatus;
  goLiveDate: string | null;
  allowAutomaticNotifications: boolean;
}

export interface PreviewPathwayDto {
  carePathwayId: string;
  carePathwayName: string;
  /**
   * Optional here although the backend field is required: the two repos deploy independently, so a
   * therapies release that lands before the trials release receives a response without it.
   */
  carePathwaySite?: PreviewCarePathwaySiteDto;
  careGaps: PreviewCareGapDto[];
}

export interface PreviewSiteDto {
  siteId: string;
  siteName: string;
  carePathways: PreviewPathwayDto[];
}

export interface PreviewBlacklistSiteDto {
  siteId: string;
  siteName: string;
  careGaps: PreviewCareGapDto[];
}

export interface PreviewNextIdCareGapDto {
  careGapId: string;
  careGapName: string;
  stageName: string;
  isEnabled: boolean;
  cutoverDate: string | null;
}

export interface PreviewNextIdPathwayDto {
  carePathwayId: string;
  carePathwayName: string;
  careGaps: PreviewNextIdCareGapDto[];
}

export interface CareGapSiteSettingsPreviewResponseDto {
  pathwaySites: PreviewSiteDto[];
  blacklistSites: PreviewBlacklistSiteDto[];
  nextId: PreviewNextIdPathwayDto[];
}

export interface CreateCareGapSiteSettingReqBody {
  careGapIds: string[];
  siteId: string;
  cutoverDate?: string | null;
  isEnabled?: boolean;
}

export interface UpdateCareGapSiteSettingReqBody {
  cutoverDate?: string;
  isEnabled?: boolean;
}

export interface CareGapSiteSettingResponseDto {
  careGapId: string;
  siteId: string;
  cutoverDate: string | null;
  isEnabled: boolean;
  createdAt: string;
  updatedAt: string;
}

// Care gap edit — mirrors the backend `CareGapResponseDto` / `UpdateCareGapDto`.
export interface CareGapResponseDto {
  id: string;
  name: string;
  defaultCutoverDate: string | null;
  isEnabledByDefault: boolean;
  notificationContent: CareGapNotificationContent | null;
  userId?: string | null;
}

export interface UpdateCareGapReqBody {
  defaultCutoverDate?: string;
  isEnabledByDefault?: boolean;
  notificationContent?: CareGapNotificationContent | null;
}

export interface CreateCarePathwaySiteReqBody {
  carePathwayId: string;
  siteId: string;
  goLiveDate: string;
}

export interface UpdateCarePathwaySiteReqBody {
  goLiveDate?: string;
  status?: ActiveStatus;
  allowAutomaticNotifications?: boolean;
}

export interface CarePathwaySiteResponseDto {
  id: string;
  carePathwayId: string;
  siteId: string;
  status: string;
  goLiveDate: string | null;
  createdAt: string;
  updatedAt: string;
}

// Narrow an unknown error (typically an AxiosError) to the Zen node-level error the
// simulate endpoint returns as a 400 when a graph fails to evaluate. Returns null for
// any other error (invalid-id 400, 404, auth, 502, …) so callers can render the failing
// node inline vs. surfacing a generic message.
export function getSimulateRulesetNodeError(error: unknown): SimulateRulesetNodeError | null {
  const data = (error as { response?: { data?: unknown } })?.response?.data;
  if (
    !!data &&
    typeof data === 'object' &&
    (data as { type?: unknown }).type === 'NodeError' &&
    typeof (data as { nodeId?: unknown }).nodeId === 'string'
  ) {
    return data as SimulateRulesetNodeError;
  }
  return null;
}
